import { NgFor } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { IListing, IUser, Listing } from '@curbnturf/entities';
import {
  DisplayValidationErrorsDirective,
  DisplayValidationErrorsSubmitButtonDirective,
  DisplayValidationErrorsSubmitDirective,
} from '@curbnturf/form-helpers';
import * as ListingActions from '@curbnturf/listing/src/lib/+state/listing.actions';
import { ListingFacade } from '@curbnturf/listing/src/lib/+state/listing.facade';
import { SignUpStateFacade } from '@curbnturf/sign-up-state/src/lib/+state/sign-up-state.facade';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule, LoadingController, ModalController } from '@ionic/angular';
import { NgSelectModule } from '@ng-select/ng-select';
import { Actions, ofType } from '@ngrx/effects';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'curbnturf-listing-add-modal',
  templateUrl: './listing-add-modal.component.html',
  styleUrls: ['./listing-add-modal.component.scss'],
  standalone: true,
  imports: [
    DisplayValidationErrorsDirective,
    DisplayValidationErrorsSubmitButtonDirective,
    DisplayValidationErrorsSubmitDirective,
    FormsModule,
    IonicModule,
    NgSelectModule,
    NgFor,
    ReactiveFormsModule,
  ],
})
export class ListingAddModalComponent implements OnInit, OnDestroy {
  @Input() currentListings: Listing[];

  duplicateForm: FormGroup<{ duplicateListing: FormControl<number | null> }>;

  currentUser: IUser;

  private subscriptions: Subscription[] = [];

  constructor(
    private modalController: ModalController,
    private router: Router,
    private listingFacade: ListingFacade,
    private fb: FormBuilder,
    private actions$: Actions,
    private loadingCtrl: LoadingController,
    private signUpFacade: SignUpStateFacade,
    private userFacade: UserFacade,
  ) {
    this.duplicateForm = this.fb.group({ duplicateListing: this.fb.control<number | null>(null) });
  }

  ngOnInit() {
    this.subscriptions.push(
      this.userFacade.currentUser$.subscribe((user) => {
        this.currentUser = user;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      if (!subscription.closed) {
        subscription.unsubscribe();
      }
    });
  }

  async close() {
    await this.modalController.dismiss();
  }

  async add(): Promise<void> {
    await this.close();
    this.signUpFacade.startNewListingAndFlow(this.currentUser);
  }

  async duplicate(): Promise<void> {
    if (this.duplicateForm.value.duplicateListing) {
      const listingId = this.duplicateForm.value.duplicateListing;
      const selectedListing: IListing = { ...this.currentListings.find((el) => el.id === listingId) };
      if (selectedListing) {
        const loading = await this.loadingCtrl.create({
          message: 'Duplicating Listing...',
          duration: 10000,
        });

        this.subscriptions.push(
          this.actions$.pipe(ofType(ListingActions.listingCreated), take(1)).subscribe(async (newListing) => {
            if (newListing.listing) {
              await loading.dismiss();
              await this.close();
              await this.router.navigate(['', 'properties', 'edit', newListing.listing.id]);
            }
          }),
        );

        // remove non-duplicatable ids
        delete selectedListing.id;
        delete selectedListing.syncId;

        if (selectedListing.landOwner?.id) {
          selectedListing.landOwner = {...selectedListing.landOwner}
          delete selectedListing.landOwner.id;
        }

        if (selectedListing.sites) {
          selectedListing.sites = [];
        }

        this.listingFacade.create(selectedListing);
        await loading.present();
      }
    }
  }
}
